import { BarChart3 } from "lucide-react"
import { DateTime } from "luxon"
import { memo, type ReactNode, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { LeakTrendsDTO } from "@/gen/api"
import useGetLeakGraph from "@/hooks/api/use-get-leak-graph"
import useGetLeakTrends from "@/hooks/api/use-get-leak-trends"

type LeakPoint = {
  period?: string
  count?: number
}

type ChartPoint = {
  time: string
  value: number
}

type ClearedRange = "year" | "month"

type LeakAlertsWidgetProps = {
  leakTrends?: LeakTrendsDTO
  isLoading?: boolean
  variant?: "council" | "master"
}

const REPORTING_ZONE = "Australia/Sydney"

const CLEARED_RANGE_OPTIONS: Array<{ value: ClearedRange; label: string }> = [
  { value: "year", label: "Year" },
  { value: "month", label: "Month" },
]

const pad2 = (value: number): string => String(value).padStart(2, "0")

const toMonthInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`

const toUtcIso = (dateTime: DateTime): string => dateTime.toUTC().toISO() ?? ""

const getYearRange = (year: number) => {
  const now = DateTime.now().setZone(REPORTING_ZONE)
  const isCurrentYear = year === now.year
  const endMonth = DateTime.fromObject(
    { year, month: isCurrentYear ? now.month : 12, day: 1 },
    { zone: REPORTING_ZONE },
  )
  const startMonth = isCurrentYear
    ? endMonth.minus({ months: 11 })
    : DateTime.fromObject({ year, month: 1, day: 1 }, { zone: REPORTING_ZONE })

  return {
    from: toUtcIso(startMonth.startOf("month")),
    to: toUtcIso(endMonth.endOf("month")),
  }
}

export const getClearedRange = (range: ClearedRange, value: string) => {
  if (range === "year") {
    const year = Number(value)
    return getYearRange(Number.isFinite(year) ? year : new Date().getFullYear())
  }

  const now = DateTime.now().setZone(REPORTING_ZONE)
  const [yearText, monthText] = value.split("-")
  const year = Number(yearText)
  const month = Number(monthText)
  const selectedMonth =
    Number.isFinite(year) && Number.isFinite(month)
      ? DateTime.fromObject({ year, month, day: 1 }, { zone: REPORTING_ZONE })
      : now.startOf("month")
  const isCurrentMonth = selectedMonth.year === now.year && selectedMonth.month === now.month
  const endDay = isCurrentMonth ? now : selectedMonth.endOf("month")
  const startDay = isCurrentMonth ? endDay.minus({ days: 30 }) : selectedMonth.startOf("month")

  return {
    from: toUtcIso(startDay.startOf("day")),
    to: toUtcIso(endDay.endOf("day")),
  }
}

const buildChartData = (points: LeakPoint[] | undefined): ChartPoint[] => {
  return (points ?? []).map((point) => ({
    time: point.period ?? "",
    value: point.count ?? 0,
  }))
}

function LeakChartCard({
  title,
  description,
  data,
  color,
  emptyLabel,
  gradientId,
  actions,
  isLoading,
}: {
  title: string
  description: string
  data: ChartPoint[]
  color: string
  emptyLabel: string
  gradientId: string
  actions?: ReactNode
  isLoading?: boolean
}) {
  const total = useMemo(() => data.reduce((sum, item) => sum + item.value, 0), [data])
  const latest = data[data.length - 1]?.value ?? 0

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
          {actions ? <div className="shrink-0">{actions}</div> : null}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading {title.toLowerCase()}...
            </div>
          ) : data.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 24 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey="time"
                  fontSize={12}
                  angle={-35}
                  height={52}
                  interval={0}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                  tickMargin={12}
                  textAnchor="end"
                />
                <YAxis
                  allowDecimals={false}
                  fontSize={12}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null
                    const point = payload[0]?.payload as ChartPoint | undefined
                    return (
                      <div className="bg-card border rounded-lg p-3 shadow-lg">
                        <p className="font-medium">{point?.time}</p>
                        <p style={{ color }}>
                          {title}: {point?.value ?? 0}
                        </p>
                      </div>
                    )
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  name={title}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name={title}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
              <BarChart3 className="size-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No data available</p>
              <p className="text-sm">{emptyLabel}</p>
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Latest {title}</span>
            <span className="font-semibold" style={{ color }}>
              {latest}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Period {title}</span>
            <span className="font-semibold" style={{ color }}>
              {total}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function LeakAlertsWidget({
  leakTrends,
  isLoading = false,
  variant = "council",
}: LeakAlertsWidgetProps = {}) {
  const { councilId } = useParams()
  const now = useMemo(() => new Date(), [])
  const [clearedRange, setClearedRange] = useState<ClearedRange>("year")
  const [clearedYear, setClearedYear] = useState(String(now.getFullYear()))
  const [clearedMonth, setClearedMonth] = useState(toMonthInputValue(now))

  const activeDateRange = useMemo(() => getYearRange(now.getFullYear()), [now])
  const clearedDateRange = useMemo(() => {
    if (clearedRange === "year") return getClearedRange(clearedRange, clearedYear)
    return getClearedRange(clearedRange, clearedMonth)
  }, [clearedMonth, clearedRange, clearedYear])

  const { data: activeLeakGraphData, isLoading: isActiveLoading } = useGetLeakGraph(
    councilId || "",
    activeDateRange.from,
    activeDateRange.to,
  )
  const { data: clearedLeakGraphData, isLoading: isClearedLoading } = useGetLeakGraph(
    councilId || "",
    clearedDateRange.from,
    clearedDateRange.to,
  )
  const isMaster = variant === "master"
  const { data: clearedMasterLeakData, isLoading: isClearedMasterLoading } = useGetLeakTrends(
    isMaster ? clearedDateRange.from : undefined,
    isMaster ? clearedDateRange.to : undefined,
  )

  const activeData = useMemo(() => {
    const source = leakTrends?.activeLeaks ?? activeLeakGraphData?.activeLeaks
    return buildChartData(source)
  }, [activeLeakGraphData, leakTrends])

  const clearedData = useMemo(() => {
    if (isMaster) {
      const source = clearedMasterLeakData?.clearedLeaks
      return buildChartData(source)
    }
    const source = leakTrends?.clearedLeaks ?? clearedLeakGraphData?.clearedLeaks
    return buildChartData(source)
  }, [clearedMasterLeakData, clearedLeakGraphData, isMaster, leakTrends])

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <LeakChartCard
        title="Active Leaks"
        description={
          isMaster
            ? "Yearly cumulative active leak total across councils"
            : "Yearly cumulative active leak total"
        }
        data={activeData}
        color="hsl(var(--destructive))"
        emptyLabel={
          isMaster ? "No active leak data across councils" : "No active leak data for this year"
        }
        gradientId={isMaster ? "masterActiveLeaks" : "councilActiveLeaks"}
        isLoading={isLoading || (!leakTrends && isActiveLoading)}
      />
      <LeakChartCard
        title="Cleared Leaks"
        description={
          isMaster
            ? "Historical cleared leak trend across councils"
            : "Historical cleared leak trend"
        }
        data={clearedData}
        color="hsl(var(--chart-2))"
        emptyLabel={
          isMaster ? "No cleared leak data across councils" : "No cleared leak data for this period"
        }
        gradientId={isMaster ? "masterClearedLeaks" : "councilClearedLeaks"}
        isLoading={isLoading || (isMaster ? isClearedMasterLoading : (!leakTrends && isClearedLoading))}
        actions={
            <div className="flex flex-col gap-2 sm:items-end">
              <div className="inline-flex rounded-md border bg-background p-1">
                {CLEARED_RANGE_OPTIONS.map((range) => (
                  <button
                    key={range.value}
                    type="button"
                    onClick={() => setClearedRange(range.value)}
                    className={`rounded-sm px-3 py-1 text-sm transition-colors ${
                      clearedRange === range.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              {clearedRange === "year" ? (
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  value={clearedYear}
                  onChange={(event) => setClearedYear(event.target.value)}
                  aria-label="Cleared Leaks year"
                  className="h-9 w-28 rounded-md border bg-background px-3 text-sm"
                />
              ) : null}
              {clearedRange === "month" ? (
                <input
                  type="month"
                  value={clearedMonth}
                  onChange={(event) => setClearedMonth(event.target.value)}
                  aria-label="Cleared Leaks month"
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                />
              ) : null}
            </div>
        }
      />
    </div>
  )
}

export default memo(LeakAlertsWidget)
